import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as invoicesApi from '../api/invoices';

export function Invoices() {
  const navigate = useNavigate();
  const { selectedOrg } = useOrg();
  const { canReadFinance } = usePermissions();
  const canViewFinance = canReadFinance;
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    siteId: ''
  });

  const loadData = useCallback(async () => {
    if (!selectedOrg || !canViewFinance) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const activeFilters = {};
      if (filters.status) activeFilters.status = filters.status;
      if (filters.type) activeFilters.type = filters.type;
      if (filters.siteId) activeFilters.siteId = filters.siteId;
      
      const result = await invoicesApi.getInvoices(selectedOrg.id, activeFilters);
      setInvoices(result.invoices);
      setTotal(result.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canViewFinance, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRowClick = async (invoiceId) => {
    if (!selectedOrg) return;
    try {
      const invoice = await invoicesApi.getInvoice(selectedOrg.id, invoiceId);
      setSelectedInvoice(invoice);
      setShowDrawer(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return { bg: '#c6f6d5', color: '#276749' };
      case 'pending': return { bg: '#fefcbf', color: '#975a16' };
      case 'overdue': return { bg: '#fed7d7', color: '#c53030' };
      case 'cancelled': return { bg: '#e2e8f0', color: '#4a5568' };
      default: return { bg: '#e2e8f0', color: '#4a5568' };
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'invoice': return { bg: '#bee3f8', color: '#2b6cb0' };
      case 'quote': return { bg: '#e9d8fd', color: '#6b46c1' };
      default: return { bg: '#e2e8f0', color: '#4a5568' };
    }
  };

  if (!canViewFinance) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You need finance permissions to view invoices and quotes.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Invoices & Quotes"
        subtitle="View service invoices and quotes linked to work orders"
      />

      <div style={styles.filterBar}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Type</label>
          <select
            value={filters.type}
            onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
            style={styles.filterSelect}
            data-testid="type-filter"
          >
            <option value="">All</option>
            <option value="invoice">Invoice</option>
            <option value="quote">Quote</option>
          </select>
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Status</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            style={styles.filterSelect}
            data-testid="status-filter"
          >
            <option value="">All</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="overdue">Overdue</option>
          </select>
        </div>
        <div style={styles.totalCount}>
          {total} record{total !== 1 ? 's' : ''}
        </div>
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading invoices...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table} data-testid="invoices-table">
            <thead>
              <tr>
                <th style={styles.th}>Number</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Vendor</th>
                <th style={styles.th}>Site</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Issue Date</th>
                <th style={styles.th}>Due Date</th>
                <th style={styles.th}>Total</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => {
                const statusStyle = getStatusColor(inv.status);
                const typeStyle = getTypeColor(inv.type);
                return (
                  <tr
                    key={inv.id}
                    style={styles.tr}
                    onClick={() => handleRowClick(inv.id)}
                    data-testid={`inv-${inv.id}`}
                  >
                    <td style={styles.td}><strong>{inv.number}</strong></td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, background: typeStyle.bg, color: typeStyle.color }}>
                        {inv.type}
                      </span>
                    </td>
                    <td style={styles.td}>{inv.vendorName}</td>
                    <td style={styles.td}>{inv.siteName}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, background: statusStyle.bg, color: statusStyle.color }}>
                        {inv.status}
                      </span>
                    </td>
                    <td style={styles.td}>{inv.issueDate}</td>
                    <td style={styles.td}>{inv.dueDate}</td>
                    <td style={styles.td}><strong>${inv.total.toFixed(2)}</strong></td>
                  </tr>
                );
              })}
              {invoices.length === 0 && (
                <tr>
                  <td colSpan="8" style={styles.emptyState}>No invoices or quotes found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showDrawer && selectedInvoice && (
        <div style={styles.drawerOverlay} onClick={() => setShowDrawer(false)}>
          <div style={styles.drawer} onClick={(e) => e.stopPropagation()} data-testid="invoice-drawer">
            <div style={styles.drawerHeader}>
              <h3 style={styles.drawerTitle}>{selectedInvoice.number}</h3>
              <button onClick={() => setShowDrawer(false)} style={styles.closeButton}>×</button>
            </div>
            
            <div style={styles.drawerContent}>
              <div style={styles.drawerSection}>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Type</span>
                  <span style={{ ...styles.badge, ...getTypeColor(selectedInvoice.type) }}>
                    {selectedInvoice.type}
                  </span>
                </div>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Status</span>
                  <span style={{ ...styles.badge, ...getStatusColor(selectedInvoice.status) }}>
                    {selectedInvoice.status}
                  </span>
                </div>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Vendor</span>
                  <span>{selectedInvoice.vendorName}</span>
                </div>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Site</span>
                  <span>{selectedInvoice.siteName}</span>
                </div>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Issue Date</span>
                  <span>{selectedInvoice.issueDate}</span>
                </div>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Due Date</span>
                  <span>{selectedInvoice.dueDate}</span>
                </div>
                {selectedInvoice.paidDate && (
                  <div style={styles.drawerRow}>
                    <span style={styles.drawerLabel}>Paid Date</span>
                    <span>{selectedInvoice.paidDate}</span>
                  </div>
                )}
              </div>

              {selectedInvoice.workOrderIds.length > 0 && (
                <div style={styles.drawerSection}>
                  <h4 style={styles.sectionTitle}>Linked Work Orders</h4>
                  <div style={styles.linkedWOs}>
                    {selectedInvoice.workOrderIds.map(woId => (
                      <button
                        key={woId}
                        onClick={() => navigate(`/service/work-orders/${woId}`)}
                        style={styles.woLink}
                        data-testid={`wo-link-${woId}`}
                      >
                        {woId}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div style={styles.drawerSection}>
                <h4 style={styles.sectionTitle}>Line Items</h4>
                <table style={styles.lineItemsTable}>
                  <thead>
                    <tr>
                      <th style={styles.lineItemTh}>Description</th>
                      <th style={styles.lineItemTh}>Qty</th>
                      <th style={styles.lineItemTh}>Unit Price</th>
                      <th style={styles.lineItemTh}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.lineItems.map((item, idx) => (
                      <tr key={idx}>
                        <td style={styles.lineItemTd}>{item.description}</td>
                        <td style={styles.lineItemTd}>{item.quantity}</td>
                        <td style={styles.lineItemTd}>${item.unitPrice.toFixed(2)}</td>
                        <td style={styles.lineItemTd}>${item.total.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={styles.totalsSection}>
                <div style={styles.totalRow}>
                  <span>Subtotal</span>
                  <span>${selectedInvoice.subtotal.toFixed(2)}</span>
                </div>
                <div style={styles.totalRow}>
                  <span>Tax</span>
                  <span>${selectedInvoice.tax.toFixed(2)}</span>
                </div>
                <div style={styles.grandTotalRow}>
                  <span>Total</span>
                  <span>${selectedInvoice.total.toFixed(2)}</span>
                </div>
              </div>

              {selectedInvoice.notes && (
                <div style={styles.drawerSection}>
                  <h4 style={styles.sectionTitle}>Notes</h4>
                  <p style={styles.notes}>{selectedInvoice.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1400px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  filterBar: { display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  filterLabel: { fontSize: '0.75rem', fontWeight: '500', color: '#4a5568' },
  filterSelect: { padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem', background: 'white', minWidth: '120px' },
  totalCount: { marginLeft: 'auto', fontSize: '0.875rem', color: '#718096' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px', marginBottom: '1rem' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  tableContainer: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.75rem 1rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '0.75rem', color: '#4a5568', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #e2e8f0', cursor: 'pointer', transition: 'background 0.15s' },
  td: { padding: '0.75rem 1rem', fontSize: '0.875rem' },
  badge: { padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', textTransform: 'capitalize' },
  emptyState: { padding: '2rem', textAlign: 'center', color: '#718096' },
  drawerOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'flex-end', zIndex: 1000 },
  drawer: { width: '500px', maxWidth: '100%', background: 'white', height: '100%', overflow: 'auto', boxShadow: '-4px 0 6px rgba(0,0,0,0.1)' },
  drawerHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0' },
  drawerTitle: { margin: 0, fontSize: '1.25rem', fontWeight: '600' },
  closeButton: { background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#718096' },
  drawerContent: { padding: '1.5rem' },
  drawerSection: { marginBottom: '1.5rem' },
  drawerRow: { display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f7fafc' },
  drawerLabel: { color: '#718096', fontSize: '0.875rem' },
  sectionTitle: { margin: '0 0 0.75rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#2d3748' },
  linkedWOs: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
  woLink: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #3182ce', background: 'white', color: '#3182ce', fontSize: '0.75rem', cursor: 'pointer' },
  lineItemsTable: { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' },
  lineItemTh: { textAlign: 'left', padding: '0.5rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontSize: '0.75rem', color: '#4a5568' },
  lineItemTd: { padding: '0.5rem', borderBottom: '1px solid #f7fafc' },
  totalsSection: { background: '#f7fafc', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem' },
  totalRow: { display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0', fontSize: '0.875rem' },
  grandTotalRow: { display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', fontSize: '1rem', fontWeight: '600', borderTop: '1px solid #e2e8f0', marginTop: '0.5rem' },
  notes: { margin: 0, fontSize: '0.875rem', color: '#4a5568' }
};
